import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CUSTOM_CATEGORIES } from 'utils/constants/casino/custom-categories';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { getCasinoParams, mountPoint } from 'utils/generic/route-manipulation';
import { NewCasinoCategory } from 'interfaces/new-casino';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import store from 'store';
import { setCasinoCustomCategories } from 'store/actions/new-casino';
import { getFavoriteEntitiesCount } from 'store/selectors/fav-data';
import {
  getCasinoOriginalCategories,
  getCasinoOriginalCategoriesSet,
  getCasinoOriginalCategoriesSetLeftSideBar,
  getCategoryCustomIds,
  getGamesConfigs
} from 'store/selectors/new-casino';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';

export const useCustomCategoriesUpdate = (
  shouldFetch = false,
  providerIdsForCategories?: string,
  leftSideBarMain?: boolean
): void => {
  const { path } = useSbRouteMatch(window.location.pathname);
  const originalCategories = useSelector(getCasinoOriginalCategories);
  const originalCategoriesSet = useSelector(getCasinoOriginalCategoriesSet);
  const categoryCustomIds = useSelector(getCategoryCustomIds);
  const leftSideBarOriginalCategoriesSet = useSelector(
    getCasinoOriginalCategoriesSetLeftSideBar
  );

  const user = useSelector(getUser);

  const casinoGamesConfigs = useSelector(getGamesConfigs);

  const categoriesSets = useMemo(() => {
    return (
      (leftSideBarMain
        ? leftSideBarOriginalCategoriesSet
        : originalCategoriesSet) ?? []
    );
  }, [leftSideBarOriginalCategoriesSet, originalCategoriesSet]);

  const isLoggedIn = useSelector(getIsLoggedIn);
  const {
    casino: casinoGamesFavoritesCount,
    providers: casinoProvidersFavoritesCount
  } = useSelector(getFavoriteEntitiesCount);

  const dispatch = useDispatch();
  const history = useHistory();

  const originalCategoriesCount = useMemo(() => {
    if (originalCategories === null) {
      return 0;
    } else {
      const categoriesIntersection = categoriesSets.length
        ? originalCategories.filter(cat => categoriesSets.includes(cat.id))
        : originalCategories;

      return categoriesIntersection.reduce((acc, i) => acc + +i.games_count, 0);
    }
  }, [
    originalCategories,
    categoriesSets,
    leftSideBarOriginalCategoriesSet,
    leftSideBarMain
  ]);

  useEffect(() => {
    if (shouldFetch && originalCategories !== null) {
      let finalCasinoGamesConfigs = casinoGamesConfigs || null;

      if (finalCasinoGamesConfigs === null) {
        const casinoGamesWidgetContainer = document.querySelector(
          `[data-widget="CasinoGames"]`
        );

        if (casinoGamesWidgetContainer) {
          finalCasinoGamesConfigs = JSON.parse(
            casinoGamesWidgetContainer.getAttribute('data-configs') || '{}'
          );
        }
      }

      CUSTOM_CATEGORIES.favorite = {
        ...CUSTOM_CATEGORIES.favorite,
        games_count: (
          casinoGamesFavoritesCount + casinoProvidersFavoritesCount
        ).toString()
      };

      const reduxStore = store.getState().newCasinoStore;
      const reduxCustomCategoryAll = reduxStore.categories.custom.find(
        ({ id }: { id: string }) => id === CUSTOM_CATEGORIES.all.id
      );

      if (!reduxCustomCategoryAll?.games_count) {
        CUSTOM_CATEGORIES.all = {
          ...CUSTOM_CATEGORIES.all,
          games_count: finalCasinoGamesConfigs?.bindWithCategories
            ? ''
            : originalCategoriesCount.toString()
        };
      } else {
        CUSTOM_CATEGORIES.all = {
          ...CUSTOM_CATEGORIES.all,
          games_count: finalCasinoGamesConfigs?.bindWithCategories
            ? reduxCustomCategoryAll?.games_count
            : originalCategoriesCount.toString()
        };
      }

      let customCategoriesToApply: NewCasinoCategory[];

      if (isLoggedIn) {
        customCategoriesToApply = [...Object.values(CUSTOM_CATEGORIES)];

        if (typeof providerIdsForCategories === 'string') {
          customCategoriesToApply = customCategoriesToApply.filter(item => {
            return !['suggested', 'special', 'specialLive'].includes(item.id);
          });
        }
      } else {
        customCategoriesToApply = [
          CUSTOM_CATEGORIES.favorite,
          CUSTOM_CATEGORIES.all
        ];

        if (typeof providerIdsForCategories !== 'string') {
          customCategoriesToApply = [
            ...customCategoriesToApply,
            CUSTOM_CATEGORIES.special,
            CUSTOM_CATEGORIES.specialLive
          ];
        }
      }

      if (SpringConfigs.DECENTRALIZED_CASINO_URL) {
        customCategoriesToApply = [CUSTOM_CATEGORIES.all];
      }

      const { categoryId: queryCategory } = getCasinoParams();

      if (
        !isLoggedIn &&
        user.pending === undefined &&
        [
          CUSTOM_CATEGORIES.suggested.id,
          CUSTOM_CATEGORIES.lastPlayed.id
        ].includes(queryCategory)
      ) {
        history.push({
          pathname: `${mountPoint(path, false, true)}/${
            CUSTOM_CATEGORIES.all.id
          }`,
          search: window.location.search
        });
      }

      customCategoriesToApply = customCategoriesToApply.filter(item => {
        return categoryCustomIds.includes(item.id);
      });

      dispatch(setCasinoCustomCategories(customCategoriesToApply));
    }
  }, [
    originalCategories,
    casinoGamesFavoritesCount,
    casinoProvidersFavoritesCount,
    originalCategoriesCount,
    isLoggedIn,
    casinoGamesConfigs,
    leftSideBarOriginalCategoriesSet,
    leftSideBarMain,
    categoryCustomIds
  ]);
};
